import { computed, ref, useId, type ComputedRef, type Ref } from 'vue'

export interface UseDisclosureOptions {
  /** Initial open state in uncontrolled mode. Ignored when `open` is supplied. */
  defaultOpen?: boolean
  /** Controlled open state. When supplied, internal state is never mutated. */
  open?: Ref<boolean>
  /** Called with the next open value on every toggle/open/close, in both modes. */
  onOpenChange?: (open: boolean) => void
}

export interface UseDisclosureTriggerAttrs {
  id: string
  'aria-expanded': boolean
  'aria-controls': string
}

interface UseDisclosureContentAttrs {
  id: string
  role: string
  'aria-labelledby': string
}

export interface UseDisclosure {
  isOpen: ComputedRef<boolean>
  toggle: () => void
  open: () => void
  close: () => void
  triggerAttrs: ComputedRef<UseDisclosureTriggerAttrs>
  contentAttrs: ComputedRef<UseDisclosureContentAttrs>
}

export function useDisclosure(options: UseDisclosureOptions = {}): UseDisclosure {
  const triggerId = useId()
  const contentId = useId()

  const isControlled = options.open !== undefined
  const internalOpen = ref(options.defaultOpen ?? false)

  const isOpen = computed(() =>
    isControlled ? (options.open as Ref<boolean>).value : internalOpen.value,
  )

  function setOpen(next: boolean): void {
    if (!isControlled) internalOpen.value = next
    options.onOpenChange?.(next)
  }

  function toggle(): void {
    setOpen(!isOpen.value)
  }

  function open(): void {
    setOpen(true)
  }

  function close(): void {
    setOpen(false)
  }

  const triggerAttrs = computed<UseDisclosureTriggerAttrs>(() => ({
    id: triggerId,
    'aria-expanded': isOpen.value,
    'aria-controls': contentId,
  }))

  const contentAttrs = computed<UseDisclosureContentAttrs>(() => ({
    id: contentId,
    role: 'region',
    'aria-labelledby': triggerId,
  }))

  return { isOpen, toggle, open, close, triggerAttrs, contentAttrs }
}
